import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "@/types/api";

/**
 * A standalone module, not created inline inside `QueryProvider`, for one
 * concrete reason: `src/lib/interceptors.ts`'s 401 handler needs to clear
 * the query cache on auth loss (so a previous session's authenticated data
 * — a profile, a wishlist — never flashes on screen for the next user of a
 * shared machine), and it can't reach into a React component's `useState`
 * to do that. Importing this same instance from both the provider and the
 * interceptor keeps it singular either way.
 *
 * Defaults chosen for a marketplace, not TanStack Query's own defaults:
 *
 * - `refetchOnWindowFocus: false` — refetching every time a user tabs back
 *   in is the default, but for a product grid/chat list that's wasted
 *   network on every alt-tab; features that genuinely need "fresh on focus"
 *   (an open chat thread) opt back in per-query.
 * - `staleTime: 30s` — treats data as fresh for a short window so rapid
 *   navigation (e.g. back to a product grid) doesn't trigger a duplicate
 *   fetch, without going stale enough to show visibly outdated prices.
 * - `retry: 1` — one retry tolerates a blip; anything beyond that just
 *   delays surfacing a real failure to the user (design-system.md §18's
 *   Network Error state expects to render promptly, not after several
 *   silent retries).
 * - Errors are typed as `ApiError` (src/types/api.ts) because
 *   src/lib/interceptors.ts guarantees every rejection from `httpClient` is
 *   already one, so query/mutation error handlers never need an
 *   `instanceof` check before reading `.status`/`.code`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

declare module "@tanstack/react-query" {
  interface Register {
    defaultError: ApiError;
  }
}
